<template>
  <b-modal v-model="show" size="lg" title="Lấy SID từ MISA AMIS Platform" no-close-on-backdrop hide-footer>
    <div class="alert alert-warning small mb-3">
      <strong><font-awesome-icon icon="exclamation-triangle" /> Lưu ý:</strong>
      SID có thời gian sống <strong>1 phút</strong>. Sau khi copy SID phải submit
      NGAY — nếu trễ phải lấy lại từ đầu.
    </div>

    <h6 class="mb-2">Bước 1: Đăng nhập MISA WeSign trước</h6>
    <p class="small text-muted mb-3">
      Mở 1 tab mới, login vào
      <a href="https://amisapp.misa.vn/wesign" target="_blank">https://amisapp.misa.vn/wesign</a>
      bằng account GADGET đã đăng ký với MISA. Sau khi login xong, quay lại đây.
    </p>

    <h6 class="mb-2">Bước 2: Click nút bên dưới để mở trang OAuth MISA</h6>
    <p class="small text-muted">
      Trang OAuth sẽ tự redirect về <code>https://google.com</code> kèm SID
      trong URL bar dạng: <code>https://google.com/?sID=&lt;SID&gt;&amp;lid=...</code>
    </p>
    <div class="mb-3">
      <button class="btn btn-success" @click="openOAuth" :disabled="!oauthUrl">
        <font-awesome-icon icon="external-link-alt" />&nbsp;Mở trang OAuth MISA
      </button>
      <div v-if="!oauthUrl" class="text-danger small mt-1">
        Chưa có Client ID trong Etc. Vui lòng điền "MISA Client ID" trước.
      </div>
      <details v-else class="small text-muted mt-2">
        <summary>Xem URL OAuth (debug)</summary>
        <code class="d-block mt-1" style="word-break: break-all">{{ oauthUrl }}</code>
      </details>
    </div>

    <h6 class="mb-2">Bước 3: Copy URL từ trình duyệt</h6>
    <p class="small text-muted">
      Sau khi MISA redirect về <code>google.com</code>, copy <strong>NGUYÊN URL bar</strong>
      (vd <code>https://google.com/?sid=xxx&amp;lid=vi&amp;env=Production</code>)
      rồi paste vào ô bên dưới. Hệ thống tự cắt lấy SID.
      <br>
      <em>Tip: Ctrl+L để focus URL bar, Ctrl+A chọn hết, Ctrl+C copy.</em>
    </p>
    <div class="mb-3">
      <label class="small">URL Google (paste cả URL)</label>
      <b-form-textarea v-model="sid" rows="3" max-rows="6"
        placeholder="Paste cả URL: https://www.google.com/?sid=...&lid=vi&env=Production"
        :disabled="submitting" />
    </div>

    <h6 class="mb-2">Bước 4: Submit để đổi SID → Access Token</h6>
    <div class="d-flex">
      <button class="btn btn-light" @click="close" :disabled="submitting">Huỷ</button>
      <button class="btn btn-primary ml-auto" @click="submit" :disabled="!sid || !sid.trim() || submitting">
        <font-awesome-icon v-if="submitting" icon="spinner" spin />
        <font-awesome-icon v-else icon="bolt" />&nbsp;Đổi SID → Token
      </button>
    </div>

    <div v-if="result" class="alert alert-success mt-3 small">
      <font-awesome-icon icon="check-circle" />&nbsp;<strong>Thành công!</strong>
      {{ result }}
    </div>

    <div v-if="errorDebug" class="alert alert-danger mt-3 small">
      <strong><font-awesome-icon icon="times-circle"/> Lỗi từ MISA:</strong>
      <div class="mt-1"><strong>Mã lỗi:</strong> {{ errorDebug.misaErrorCode || '(none)' }}</div>
      <div><strong>Mô tả:</strong> {{ errorDebug.misaErrorMsg || '(none)' }}</div>
      <div><strong>X-Request-ID (gửi MISA tra log):</strong> <code>{{ errorDebug.misaXRequestId || '(none)' }}</code></div>
      <div><strong>HTTP status:</strong> {{ errorDebug.misaHttpStatus }}</div>
      <details class="mt-2">
        <summary>Debug đầy đủ (copy nếu cần gửi MISA)</summary>
        <pre class="mt-1 mb-0" style="font-size:11px; max-height:300px; overflow:auto;">{{ JSON.stringify(errorDebug, null, 2) }}</pre>
        <button class="btn btn-sm btn-light mt-1" @click="copyDebug">
          <font-awesome-icon icon="copy"/>&nbsp;Copy debug JSON
        </button>
      </details>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'GetSidModal',
  data() {
    return {
      show: false,
      clientId: '',
      sid: '',
      submitting: false,
      result: null,
      errorDebug: null,
    };
  },
  computed: {
    oauthUrl() {
      if (!this.clientId) return '';
      return `https://amisapp.misa.vn/login?clientID=${encodeURIComponent(this.clientId)}&redirectUri=https://google.com`;
    },
  },
  methods: {
    async open() {
      this.show = true;
      this.sid = '';
      this.result = null;
      this.errorDebug = null;
      this.submitting = false;
      try {
        await this.$bgService.get('/admin-esign/config');
        const etcRes = await this.$bgService.get('/etc/key/esign-admin-client-id');
        this.clientId = (etcRes.data && etcRes.data.value) || '';
      } catch (e) {
        this.clientId = '';
      }
    },
    copyDebug() {
      const ta = document.createElement('textarea');
      ta.value = JSON.stringify(this.errorDebug, null, 2);
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      this.$swal({title: 'Đã copy debug', icon: 'success', toast: true, position: 'top-end', timer: 1500, showConfirmButton: false});
    },
    close() {
      this.show = false;
      this.$emit('done', !!this.result);
    },
    openOAuth() {
      if (!this.oauthUrl) return;
      window.open(this.oauthUrl, '_blank');
    },
    async submit() {
      const sid = (this.sid || '').trim();
      if (!sid) return;
      this.submitting = true;
      this.result = null;
      this.errorDebug = null;
      try {
        const response = await this.$bgService.post('/admin-esign/exchange-sid', { sid });
        if (response.data && response.data.success) {
          this.result = response.data.message || 'OK';
          this.$swal({
            title: 'Đổi SID → Token thành công',
            icon: 'success',
            toast: true,
            position: 'top-end',
            timer: 3000,
            showConfirmButton: false,
          });
          setTimeout(() => this.close(), 1500);
        } else {
          this.errorDebug = response.data;
        }
      } catch (error) {
        // BE trả 502 với debug đầy đủ trong body — hiển thị lên UI để admin copy gửi MISA
        if (error.response && error.response.data) {
          this.errorDebug = error.response.data;
        } else {
          this.$swal({text: error.message || 'Đổi SID thất bại', icon: 'error'});
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
